import { useEffect, useState } from "react";
import { BarChart3, Sparkles } from "lucide-react";
import { api } from "../../lib/api";

const DAY_LABEL = (iso) =>
  new Date(iso + "T12:00:00").toLocaleDateString(undefined, {
    weekday: "short",
  });

// Friendly weekly note: current streak (ending today or yesterday), active
// days, or a gentle nudge when the week is quiet.
const insightFor = (name, days) => {
  const first = (name || "").split(" ")[0];
  const active = days.map((d) => d.voice_minutes + d.visual_minutes > 0);
  let streak = 0;
  // Allow the streak to end today OR yesterday (today may not be played yet).
  for (
    let i = active.length - 1 - (active[active.length - 1] ? 0 : 1);
    i >= 0 && active[i];
    i--
  )
    streak++;
  const activeDays = active.filter(Boolean).length;
  if (streak >= 3)
    return `${first} practiced ${streak} days in a row — their best run this week!`;
  if (streak === 2)
    return `${first} practiced 2 days in a row — one more makes a streak!`;
  if (activeDays >= 1)
    return `${first} practiced on ${activeDays} of the last 7 days — every session counts.`;
  return `No practice yet this week — Pip is ready whenever ${first} is!`;
};

const Bars = ({ days }) => {
  const max = Math.max(
    5,
    ...days.map((d) => d.voice_minutes + d.visual_minutes),
  );
  return (
    <div
      className="flex items-end gap-2 h-32"
      role="img"
      aria-label="7 day usage chart of voice and visual learning minutes"
    >
      {days.map((d) => {
        const vh = Math.round((d.voice_minutes / max) * 100);
        const sh = Math.round((d.visual_minutes / max) * 100);
        const total = Math.round(d.voice_minutes + d.visual_minutes);
        return (
          <div
            key={d.date}
            className="flex-1 flex flex-col items-center gap-1 min-w-0"
            aria-label={`${DAY_LABEL(d.date)}: ${d.voice_minutes} minutes voice practice, ${d.visual_minutes} minutes visual learning`}
            title={`${d.date}: ${d.voice_minutes} min voice, ${d.visual_minutes} min visual`}
          >
            <span className="text-[10px] font-bold text-muted-foreground tabular-nums">
              {total > 0 ? `${total}m` : "0"}
            </span>
            <div
              className="w-full max-w-[34px] flex flex-col justify-end rounded-t-md overflow-hidden border-b-2 border-border"
              style={{ height: "88px" }}
            >
              {total > 0 ? (
                <>
                  <div
                    className="w-full bg-sky-300 transition-[height] duration-500"
                    style={{ height: `${sh}%` }}
                  />
                  <div
                    className="w-full bg-emerald-500 transition-[height] duration-500"
                    style={{ height: `${vh}%` }}
                  />
                </>
              ) : (
                <div className="w-full h-[3px] bg-muted" />
              )}
            </div>
            <span className="text-[10px] font-semibold text-muted-foreground">
              {DAY_LABEL(d.date)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Usage Trends: 7-day stacked bars of Live Voice Practice vs Visual Learning
// minutes per child, so parents can see engagement and the impact of limits.
export const UsageTrends = () => {
  const [kids, setKids] = useState([]);
  const [trends, setTrends] = useState({});

  useEffect(() => {
    api
      .get("/children")
      .then(({ data }) => {
        setKids(data);
        data.forEach((k) =>
          api
            .get(`/children/${k.child_id}/usage-trends`)
            .then(({ data: t }) =>
              setTrends((s) => ({ ...s, [k.child_id]: t.days })),
            )
            .catch(() => {}),
        );
      })
      .catch(() => {});
  }, []);

  if (!kids.length) return null;
  return (
    <div
      className="bg-card rounded-2xl border border-border p-5 space-y-4"
      data-testid="usage-trends-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold font-display flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-secondary" aria-hidden="true" />{" "}
            Usage trends — last 7 days
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Daily minutes of live voice practice vs. visual learning (games,
            mouth guides, math).
          </p>
        </div>
        <div className="flex items-center gap-3 text-[11px] font-semibold">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-emerald-500 inline-block" />{" "}
            Live Voice Practice
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-sky-300 inline-block" />{" "}
            Visual Learning
          </span>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {kids.map((k) => (
          <div
            key={k.child_id}
            className="rounded-xl border border-border px-4 py-3"
            data-testid={`usage-trend-${k.child_id}`}
          >
            <p className="text-sm font-bold mb-2">{k.display_name}</p>
            {trends[k.child_id] ? (
              <>
                <Bars days={trends[k.child_id]} />
                <p
                  className="mt-2 text-xs font-semibold text-secondary flex items-start gap-1.5"
                  data-testid={`usage-insight-${k.child_id}`}
                >
                  <Sparkles
                    className="h-3.5 w-3.5 shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  {insightFor(k.display_name, trends[k.child_id])}
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">Loading…</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
